import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBookOpen, FiPlus, FiEdit2, FiTrash2, FiCheck, FiAlertCircle, FiSearch, FiPhone, FiSmartphone, FiMail, FiGlobe } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';

const cfg = () => {
  const token = localStorage.getItem('authToken');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const BLANK = {
  county: '', city: '', category: 'Planning & Zoning', office_name: '', contact_name: '',
  title: '', email: '', phone: '', cell_phone: '', website: '', address: '', notes: '',
};

// "City of Calhoun" for a city office, else "Gordon County".
const jurisdictionOf = (r) => (r.city ? `City of ${r.city}` : (r.county ? `${r.county} County` : 'Other'));

// County/agency directory — so you can email "Gordon County Planning & Zoning"
// without hunting for the address.
export default function AgencyDirectory() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);   // row being edited, or BLANK for new
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/agency-contacts${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`, cfg());
      setRows(res.data.contacts || []);
      setCats(res.data.categories || []);
    } catch (e) { setRows([]); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const save = async () => {
    if (!editing.county && !editing.city && !editing.office_name) {
      setNote('Enter a county or a city.'); return;
    }
    setSaving(true); setNote('');
    try {
      if (editing.id) await axios.put(`${API_URL}/agency-contacts/${editing.id}`, editing, cfg());
      else await axios.post(`${API_URL}/agency-contacts`, editing, cfg());
      setEditing(null);
      await load();
    } catch (e) {
      setNote(e.response?.data?.error || 'Could not save.');
    } finally { setSaving(false); }
  };

  const verify = async (row) => {
    try { await axios.post(`${API_URL}/agency-contacts/${row.id}/verify`, { verified: true }, cfg()); await load(); }
    catch (e) { setNote('Could not verify.'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.display_name}?`)) return;
    try { await axios.delete(`${API_URL}/agency-contacts/${row.id}`, cfg()); await load(); }
    catch (e) { setNote('Could not delete.'); }
  };

  const field = (key, label, type = 'text') => (
    <div style={{ marginBottom: 8 }}>
      <label style={{ display: 'block', fontSize: 11.5, color: '#64748b', marginBottom: 3 }}>{label}</label>
      {type === 'select' ? (
        <select value={editing[key] || ''} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
          style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13.5 }}>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={editing[key] || ''} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} rows={2}
          style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13.5, boxSizing: 'border-box', fontFamily: 'inherit' }} />
      ) : (
        <input value={editing[key] || ''} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
          style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13.5, boxSizing: 'border-box' }} />
      )}
    </div>
  );

  // Group by county; within each county, county-level offices first, then each city
  // that sits in it nested underneath. A city with no county set falls into "Other".
  const groups = {};
  (rows || []).forEach(r => {
    const ck = r.county || 'Other';
    const g = (groups[ck] = groups[ck] || { countyRows: [], cities: {} });
    if (r.city) {
      (g.cities[r.city] = g.cities[r.city] || []).push(r);
    } else {
      g.countyRows.push(r);
    }
  });
  const countyKeys = Object.keys(groups).sort((a, b) => {
    if (a === 'Other') return 1;              // ungrouped last
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });

  // Split a jurisdiction's rows into one bucket per department, so a department with
  // several people shows as ONE card instead of a card per person.
  const byCategory = (list) => {
    const m = {};
    (list || []).forEach(r => { (m[r.category || 'Other'] = m[r.category || 'Other'] || []).push(r); });
    return Object.keys(m).sort().map(cat => [cat, m[cat]]);
  };

  // One card per department; each person in that department is a row inside it.
  const deptCard = (cat, list) => (
    <div key={cat} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
        {cat}
        {list.length > 1 && (
          <span style={{ fontWeight: 600, fontSize: 11.5, color: '#94a3b8' }}> · {list.length} contacts</span>
        )}
      </div>
      {list.map((r, i) => (
        <div key={r.id} style={{
          borderTop: i ? '1px solid #f1f5f9' : 'none',
          paddingTop: i ? 9 : 0, marginTop: i ? 9 : 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#334155' }}>
              {r.contact_name
                ? <strong>{r.contact_name}</strong>
                : <span style={{ color: '#64748b' }}>{r.office_name || 'Department contact'}</span>}
              {r.title ? ` · ${r.title}` : ''}
            </span>
            {!r.verified && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 10 }}>
                <FiAlertCircle size={10} /> UNVERIFIED
              </span>
            )}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              {r.email && (
                <button
                  onClick={() => navigate(`/email?to=${encodeURIComponent(r.email)}&subject=${encodeURIComponent(`${jurisdictionOf(r)} ${r.category}`)}`)}
                  title={`Email ${r.email} from the app`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#14532d', color: 'white', border: 'none', borderRadius: 6, padding: '3px 9px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                  <FiMail size={11} /> Email
                </button>
              )}
              {!r.verified && (
                <button onClick={() => verify(r)} title="Mark as confirmed correct"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dcfce7', color: '#166534', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                  <FiCheck size={11} /> Verify
                </button>
              )}
              <button onClick={() => setEditing({ ...r })} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><FiEdit2 size={13} /></button>
              <button onClick={() => remove(r)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><FiTrash2 size={13} /></button>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 4, fontSize: 12.5 }}>
            {r.email && <a href={`mailto:${r.email}`} style={{ color: '#334155' }}><FiMail size={11} /> {r.email}</a>}
            {r.phone && (
              <a href={`tel:${r.phone}`} style={{ color: '#334155' }}>
                <FiPhone size={11} /> {r.phone}
                {r.cell_phone && <span style={{ color: '#94a3b8' }}> office</span>}
              </a>
            )}
            {r.cell_phone && (
              <a href={`tel:${r.cell_phone}`} style={{ color: '#334155' }}>
                <FiSmartphone size={11} /> {r.cell_phone}<span style={{ color: '#94a3b8' }}> cell</span>
              </a>
            )}
            {r.website && <a href={r.website} target="_blank" rel="noopener noreferrer" style={{ color: '#14532d' }}><FiGlobe size={11} /> site</a>}
          </div>
          {r.notes && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{r.notes}</div>}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ padding: 20, maxWidth: 950, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <h2 style={{ margin: 0, fontSize: 22, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiBookOpen /> Agency Directory
        </h2>
        <button onClick={() => setEditing({ ...BLANK })}
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: '#14532d', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          <FiPlus size={14} /> Add Contact
        </button>
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
        County <em>and city</em> officials — Planning &amp; Zoning, Engineering, Tax Assessor, Water/Sewer
        and more. These show up in the email “To” box: type e.g. <strong>“gordon planning”</strong> or{' '}
        <strong>“calhoun engineering”</strong>.
      </p>

      <div style={{ position: 'relative', marginBottom: 14 }}>
        <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search county, city, agency, or person…"
          style={{ width: '100%', padding: '9px 10px 9px 30px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' }} />
      </div>

      {note && <div style={{ background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>{note}</div>}

      {editing && (
        <div style={{ background: 'white', border: '1px solid #bbf7d0', borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>{editing.id ? 'Edit contact' : 'New contact'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
            <div>{field('county', 'County')}{field('city', 'City (for city officials)')}{field('category', 'Category', 'select')}{field('office_name', 'Office name (optional)')}</div>
            <div>{field('contact_name', 'Contact person')}{field('title', 'Title')}{field('email', 'Email')}</div>
            <div>{field('phone', 'Office phone')}{field('cell_phone', 'Cell')}{field('website', 'Website')}{field('address', 'Address')}</div>
          </div>
          {field('notes', 'Notes', 'textarea')}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#14532d', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(null)}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#334155', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {rows === null ? (
        <div style={{ color: '#6b7280' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 12, padding: 30, textAlign: 'center', color: '#6b7280', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {q ? `No contacts match “${q}”.` : 'No agency contacts yet — add your first one.'}
        </div>
      ) : (
        countyKeys.map(ck => (
          <div key={ck} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: 6 }}>
              {ck === 'Other' ? 'Other' : `${ck} County`}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {byCategory(groups[ck].countyRows).map(([cat, list]) => deptCard(cat, list))}
            </div>
            {Object.keys(groups[ck].cities).sort().map(cityName => (
              <div key={cityName} style={{ marginTop: 10, marginLeft: 14, paddingLeft: 12, borderLeft: '2px solid #e2e8f0' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#94a3b8', marginBottom: 6 }}>
                  City of {cityName}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {byCategory(groups[ck].cities[cityName]).map(([cat, list]) => deptCard(cat, list))}
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

